/**
 * The one place a pose is pushed onto the skeleton, and the clock that drives playback.
 *
 * Two rules keep the modes honest:
 *  - ANIMATE applies `state.livePose` — and `state.liveHipsY` beside it — to the rig every frame.
 *    `BoundRig.applyPose` returns bones absent from the pose to their rest local (D18), so a clip
 *    that only animates the arms never leaves the legs frozen in whatever the last clip did. The
 *    same is true of the hips height: `applyPose(pose)` with ONE argument resets it to bind, so
 *    every call here that has a live value passes it.
 *  - RIG applies only the test-pose preview (`state.rig.previewPose`), and only once the skin
 *    exists: that pose is state, not a hand edit, so the rest rig it is checked against stays
 *    exactly what the user placed. Clearing the preview — or leaving RIG, or losing the skin —
 *    resets to rest once. MAP never touches rotations at all; leaving ANIMATE resets to rest.
 *    While a preview stands the joint gizmo is withheld (`gizmos.ts`), because dragging a joint
 *    on a posed skeleton would write a placement the user never saw.
 *
 * Read-only companions (`session.companions`) ride along on the ANIMATE rule only: same pose, same
 * reset on leaving ANIMATE, never the RIG preview.
 *
 * The viewport also owns the transport clock: `requestAnimationFrame` is the only real time
 * source in the app, so while playing it calls `store.actions.tick(dt)`. `dt` is clamped so a
 * backgrounded tab that wakes up after ten seconds resumes rather than teleporting to the end of
 * the clip.
 */

import type { Pose } from '../../shared';
import type { TestPoseId } from '../../rig/autoskin/types';
import { testPoseById } from '../../rig/autoskin/testPoses';
import type { EditorContext } from '../context';

/** A single frame longer than this is a stall (tab switch, breakpoint), not elapsed animation. */
export const MAX_TICK_SECONDS = 0.1;

export interface PoseDriver {
  /** Advance the transport if playing. Call before reading state for the frame. */
  tick(deltaSeconds: number): void;
  /** Push the current pose onto the skeleton and refresh world matrices. */
  apply(): void;
  dispose(): void;
}

export function clampDelta(deltaSeconds: number): number {
  if (!Number.isFinite(deltaSeconds) || deltaSeconds <= 0) return 0;
  return Math.min(deltaSeconds, MAX_TICK_SECONDS);
}

export function createPoseDriver(ctx: EditorContext): PoseDriver {
  let lastMode = ctx.store.getState().mode;
  /** Preview currently standing on the skeleton, so the reset to rest happens exactly once. */
  let lastAppliedPreview: TestPoseId | null = null;
  /** Resolved once per id change: `apply` runs every frame and must not do lookups or allocate. */
  let previewPose: Pose | null = null;

  function tick(deltaSeconds: number): void {
    const state = ctx.store.getState();
    if (!state.playhead.playing) return;
    const dt = clampDelta(deltaSeconds);
    if (dt > 0) ctx.store.actions.tick(dt);
  }

  function apply(): void {
    const state = ctx.store.getState();
    const rig = ctx.session.rig;
    const mode = state.mode;

    if (mode !== lastMode) {
      // Leaving ANIMATE: drop the pose so RIG/MAP/MODEL show the true rest skeleton. Companions
      // are driven by the same pose, so they go back to rest on the same transition — a preview
      // character frozen mid-stride beside a rest-pose model reads as a bug.
      if (lastMode === 'animate') {
        rig?.resetToRest();
        for (const companion of ctx.session.companions) companion.rig.resetToRest();
      }
      lastMode = mode;
    }

    // The *effective* preview: a pose id only counts while RIG owns the frame, the skin exists
    // and a rig is bound. Any of those falling away is a transition to "no preview", which is
    // what makes the single reset below cover mode changes and rig swaps as well as clearing.
    const preview =
      mode === 'rig' && state.rig.skinGenerated && rig ? state.rig.previewPose : null;
    if (preview !== lastAppliedPreview) {
      if (preview === null) {
        rig?.resetToRest();
        previewPose = null;
      } else {
        previewPose = testPoseById(preview).pose;
      }
      lastAppliedPreview = preview;
    }

    if (rig) {
      if (mode === 'animate') {
        rig.applyPose(state.livePose, state.liveHipsY);
      } else if (previewPose) {
        // The RIG test pose is about the skin, not about the clip: the hips stay at bind height
        // (one argument, which `applyPose` reads as `hipsY = 0`).
        rig.applyPose(previewPose);
      }
      // Refresh world matrices unconditionally: the gizmo, the skeleton overlay and the IK handles
      // all read world positions this frame, and in RIG/MAP the skeleton may have been rebuilt.
      const root = ctx.session.model?.root;
      if (root) root.updateMatrixWorld(true);
    }

    // Companions get the ANIMATE pose and nothing else — never the RIG test-pose preview, which
    // is about *this* model's skin, not about the clip. Their roots sit outside `model.root`, so
    // each one refreshes its own world matrices; a companion with no rig would simply not be one.
    // The loop runs even when the open project has no rig: previewing a clip beside an unmapped
    // model is exactly when a companion earns its keep.
    if (mode === 'animate') {
      for (const companion of ctx.session.companions) {
        // The same NORMALIZED offset, not the same distance: each companion scales it by its own
        // leg length, so a crouch reads as a crouch on a character half the height.
        companion.rig.applyPose(state.livePose, state.liveHipsY);
        companion.root.updateMatrixWorld(true);
      }
    }
  }

  function dispose(): void {
    if (lastMode === 'animate' || lastAppliedPreview !== null) {
      ctx.session.rig?.resetToRest();
      for (const companion of ctx.session.companions) companion.rig.resetToRest();
    }
    lastAppliedPreview = null;
    previewPose = null;
  }

  return { tick, apply, dispose };
}
